import React, { useEffect, useMemo } from 'react';
import { StyleSheet, Text, TouchableOpacity, View } from './rn';
import { radius, type ThemeColors } from '../theme';
import { useTheme } from '../theme/ThemeContext';
import { useSafeAreaInsets } from 'react-native-safe-area-context';
import type {
  ForegroundEventNotification,
  ForegroundMessageNotification,
} from '../notifications/pushLifecycle';

type Props = {
  notification: ForegroundMessageNotification | ForegroundEventNotification;
  fallbackBody: string;
  onPress: () => void;
  onDismiss: () => void;
};

const DISPLAY_MS = 5000;

/** D-127 / 9.26.10 — uygulama öndeyken ses üretmeyen, hedefe giden mesaj banner'ı. */
export default function ForegroundNotificationBanner({
  notification,
  fallbackBody,
  onPress,
  onDismiss,
}: Props) {
  const colors = useTheme();
  const insets = useSafeAreaInsets();
  const styles = useMemo(() => makeStyles(colors), [colors]);
  const title = notification.title ?? fallbackBody;
  const bodyCandidate = notification.body ?? fallbackBody;
  const body = bodyCandidate !== title ? bodyCandidate : null;

  useEffect(() => {
    const timer = setTimeout(onDismiss, DISPLAY_MS);
    return () => clearTimeout(timer);
  }, [notification, onDismiss]);

  return (
    <TouchableOpacity
      style={[styles.banner, { top: insets.top + 8 }]}
      onPress={onPress}
      activeOpacity={0.82}
      accessible
      accessibilityRole="button"
      accessibilityLabel={body ? `${title}. ${body}` : title}>
      <View style={styles.textWrap}>
        <Text style={styles.title} numberOfLines={1}>{title}</Text>
        {body && <Text style={styles.body} numberOfLines={1}>{body}</Text>}
      </View>
      <Text style={styles.chevron} accessibilityElementsHidden>›</Text>
    </TouchableOpacity>
  );
}

const makeStyles = (colors: ThemeColors) => StyleSheet.create({
  banner: {
    position: 'absolute',
    left: 12,
    right: 12,
    minHeight: 64,
    zIndex: 1400,
    elevation: 14,
    flexDirection: 'row',
    alignItems: 'center',
    paddingHorizontal: 16,
    paddingVertical: 11,
    borderRadius: radius.card,
    borderWidth: StyleSheet.hairlineWidth,
    borderColor: colors.gray200,
    backgroundColor: colors.white,
    shadowColor: '#000000',
    shadowOffset: { width: 0, height: 8 },
    shadowOpacity: 0.18,
    shadowRadius: 18,
  },
  textWrap: { flex: 1, minWidth: 0 },
  title: { color: colors.black, fontSize: 15, fontWeight: '700' },
  body: { color: colors.gray600, fontSize: 14, marginTop: 2 },
  chevron: { color: colors.gray400, fontSize: 28, lineHeight: 30, marginLeft: 12 },
});
